import { useEffect } from 'react'
import { State, useHookstate } from '@hookstate/core'
import { BaseRole, RolePermissions } from '../../domain/role'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { Module, ModulePerms, SubModule, SubModulePerms } from '../../domain/module'
import { Button, Switch, message } from 'antd'
import {
  ArrowLeftOutlined,
  BarChartOutlined,
  CodeSandboxOutlined,
  ContactsOutlined,
  ContainerOutlined,
  DollarOutlined,
  DotChartOutlined,
  FileOutlined,
  FlagOutlined,
  HistoryOutlined,
  MailOutlined,
  NodeIndexOutlined,
  ProfileOutlined,
  RightOutlined,
  SettingOutlined,
  ShoppingCartOutlined,
  StarOutlined,
  TeamOutlined
} from '@ant-design/icons'
import Icons from '../../../../../Main/Components/Icons'
import { SearchableSingleSelect } from '../../../../kernel/infrastructure/searchable-single-select.component'
import {
  ActiveUsersTag,
  ConfigBody,
  ConfigContainer,
  ConfigModuleList,
  DetailSection,
  DetailsTitle,
  HeadSection,
  Header,
  ModuleActivationCard,
  ModuleListItem,
  OffTag,
  PermissionControls,
  PermissionItem,
  ModulePermissions,
  PermissionsHeader,
  PermissionsList,
  PermissionsTitle,
  RoleDetails,
  RoleIcon,
  RoleTitle,
  SubModulesList,
  Title,
  Footer
} from './partials.component'
import { UnsavedChangesModal } from './unsaved-changes-modal.component'
import { getModulesUseCase } from '../../application/get-modules.use-case'
import { getModulePermissionsUseCase } from '../../application/get-module-permissions.use-case'
import { odinModuleRepository } from '../odin-module.repository'
import { CollapsibleSubModule } from './collapsible-sub-module.component'
import { getRolePermissionsUseCase } from '../../application/get-role-permissions.use-case'
import { assignRolePermissionsUseCase } from '../../application/asign-role-permissions.use-case'
import { odinRoleRepository } from '../odin-role.repository'
import { removeRolePermissionsUseCase } from '../../application/remove-role-permission.use-case'
import { useGate } from 'statsig-react'
import { ShoppingBag } from '@phosphor-icons/react'

type option = {
  label: string
  value: any
  hierarchy: number
  superAdmin: boolean
  icon?: () => JSX.Element
}
type RolesConfigProps = {
  selectedRole: State<BaseRole, {}>
  currentUser: any
  getRoleIcon: (name: string, isSuperAdmin: boolean) => JSX.Element
  roleList: State<BaseRole[], {}>
  getRoleTranslation: (name: string) => string
}

export const getIconAndTranslationByModuleId = (id: string): { icon: JSX.Element; translation: string } => {
  let translation: string = ''
  let icon: JSX.Element = <></>
  switch (id) {
    case ModulePerms.JOB:
      translation = 'menu.jobs-search'
      icon = <ProfileOutlined />
      break
    case ModulePerms.FLA:
      translation = 'menu.jobs-list'
      icon = <FlagOutlined />
      break
    case ModulePerms.ROU:
      translation = 'menu.routes'
      icon = <NodeIndexOutlined />
      break
    case ModulePerms.MET:
      translation = 'metrics'
      icon = <DotChartOutlined />
      break
    case ModulePerms.ANA:
      translation = 'metabase.analytics'
      icon = <BarChartOutlined />
      break
    case ModulePerms.USE:
      translation = 'menu.users'
      icon = <TeamOutlined />
      break
    case ModulePerms.RES:
      translation = 'menu.resources'
      icon = <ContactsOutlined />
      break
    case ModulePerms.PAR:
      translation = 'systemParameters'
      icon = <SettingOutlined />
      break
    case ModulePerms.ACC:
      translation = 'Accounting'
      icon = <DollarOutlined />
      break
    case ModulePerms.ECO:
      translation = 'E-commerce Admin'
      icon = <ShoppingCartOutlined />
      break
    case ModulePerms.WMS:
      translation = 'WMS'
      icon = <CodeSandboxOutlined />
      break
    case ModulePerms.SEG:
      translation = 'Segmenter'
      icon = <MailOutlined />
      break
    case ModulePerms.COM:
      translation = 'Comanda'
      icon = <Icons iconName='TablePoint' />
      break
    case ModulePerms.ORA:
      translation = 'order-ratings'
      icon = <StarOutlined />
      break
    case ModulePerms.JJS:
      translation = 'Jonah jameson'
      icon = <MailOutlined />
      break
    case ModulePerms.LOG:
      translation = 'activityLog.activityLog'
      icon = <HistoryOutlined />
      break
    case ModulePerms.SST:
      translation = 'reports.title'
      icon = <FileOutlined />
      break
    case ModulePerms.CAL:
      translation = 'Call Center'
      icon = <Icons iconName='Phone' />
      break
    case ModulePerms.TAS:
      translation = 'menu.tasks'
      icon = <ContainerOutlined />
      break
    case ModulePerms.PIC:
      translation = 'picking-web'
      icon = <ShoppingBag />
      break
  }
  return { icon, translation }
}

const RolesConfig = ({ selectedRole, getRoleIcon, roleList, getRoleTranslation, currentUser }: RolesConfigProps) => {
  const { t } = useTranslation()
  const history = useHistory()
  const getModulePermissionsUseCaseInteractor = getModulePermissionsUseCase(odinModuleRepository)
  const getRolePermissionsUseCaseInteractor = getRolePermissionsUseCase(odinRoleRepository)
  const getModulesUseCaseInteractor = getModulesUseCase(odinModuleRepository)
  const assignRolePermissionsUseCaseInteractor = assignRolePermissionsUseCase(odinRoleRepository)
  const removeRolePermissionsUseCaseInteractor = removeRolePermissionsUseCase(odinRoleRepository)
  const seledtedModuleIndex = useHookstate<number>(0)
  const selectedModule = useHookstate<Module>({} as Module)
  const rolesOptions = useHookstate<option[]>([])
  const showModulePermissions = useHookstate<boolean>(true)
  const modules = useHookstate<Module[]>([])
  const subModules = useHookstate<SubModule[]>([])
  const showFooter = useHookstate<boolean>(false)
  const showModal = useHookstate<boolean>(false)
  const rolePermissions = useHookstate<string[]>([])
  const rolePermissionsToEdit = useHookstate<string[]>([])
  const roleOptionSelected = useHookstate<option>({} as option)
  const deniedSuperAdminPermissions = [ModulePerms.CAL, SubModulePerms.FLA_GENERAL_AUTOMATIC_ASSIGN]
  const initialState = {}
  Object.entries(subModules.get()).forEach((subModule, index) => {
    initialState[index] = false
  })
  const displayValues = useHookstate(initialState)
  const isRoleHierarchyActive = useGate('is_roles_hierarchy_active').value

  const getModules = async () => {
    const modulesResult: Module[] = await getModulesUseCaseInteractor()
    modules.set(modulesResult)
  }
  const getModulePermissions = async (moduleId: string) => {
    const modulePermissionsResponse: SubModule[] = await getModulePermissionsUseCaseInteractor(moduleId)
    if (moduleId === ModulePerms.USE) {
      modulePermissionsResponse.forEach((subModule) => {
        if (subModule.id === 'USE_USER') {
          subModule.permissions = subModule.permissions.filter((permission) => !permission.id.includes('CLIENT'))
        }
      })
    }
    if (moduleId === ModulePerms.PAR) {
      let subModuleIndex = modulePermissionsResponse.findIndex((subModule) => subModule.id === 'PAR_GENERAL')
      modulePermissionsResponse.splice(subModuleIndex, 1)
    }
    subModules.set(modulePermissionsResponse)
  }
  const getRolePermissions = async (roleId: string) => {
    const rolePermissionsResponse: RolePermissions = await getRolePermissionsUseCaseInteractor(roleId)
    rolePermissions.set(rolePermissionsResponse.permissions)
    rolePermissionsToEdit.set(rolePermissionsResponse.permissions)
  }

  const validatePermission = (permissionId: string): boolean => {
    if (!permissionId) return false
    if (selectedRole.get().superAdmin && deniedSuperAdminPermissions.includes(permissionId as ModulePerms)) return false
    if (selectedRole.get().superAdmin) return true
    return rolePermissionsToEdit.get().includes(permissionId)
  }
  const onChangePermission = (permissionId: string) => {
    if (validatePermission(permissionId)) {
      rolePermissionsToEdit.set(rolePermissionsToEdit.get().filter((permission) => permission !== permissionId))
    } else {
      rolePermissionsToEdit.set([...rolePermissionsToEdit.get(), permissionId])
    }
    if (!showFooter.get() && !selectedRole.get().superAdmin) showFooter.set(true)
  }
  const onCancelPermissionsChanged = () => {
    rolePermissionsToEdit.set(JSON.parse(JSON.stringify(rolePermissions.get())))
    showFooter.set(false)
  }
  const onSavePermissionsChanged = async () => {
    let permissionsAssigned = true
    let permissionsRemoved = true
    const permissionsToAdd = rolePermissionsToEdit
      .get()
      .filter((permission) => !rolePermissions.get().includes(permission))
    const permissionsToDelete = rolePermissions
      .get()
      .filter((permission) => !rolePermissionsToEdit.get().includes(permission))

    if (permissionsToAdd.length === 0 && permissionsToDelete.length === 0) {
      return
    }
    if (permissionsToDelete.length > 0) {
      permissionsRemoved = await removeRolePermissionsUseCaseInteractor(selectedRole.get().id, permissionsToDelete)
    }
    if (permissionsToAdd.length > 0) {
      permissionsAssigned = await assignRolePermissionsUseCaseInteractor(selectedRole.get().id, permissionsToAdd)
    }
    if (permissionsAssigned && permissionsRemoved) {
      message.success(t('roles-permissions.permissions-updated'))
      rolePermissions.set(JSON.parse(JSON.stringify(rolePermissionsToEdit.get())))
    }
    showFooter.set(false)
  }
  const onChangeModule = (module: Module, index: number) => {
    if (!module.activeForClient) return
    seledtedModuleIndex.set(index)
    selectedModule.set(JSON.parse(JSON.stringify(module)))
    showModulePermissions.set(true)
  }
  const onChangeRole = (option: option) => {
    const permissionsChanged = showFooter.get()
    if (permissionsChanged) {
      roleOptionSelected.set({
        label: option.label,
        value: option.value,
        superAdmin: option.superAdmin,
        hierarchy: option.hierarchy
      })
      showModal.set(true)
      return
    }
    selectedRole.set(JSON.parse(JSON.stringify(roleList.get().find((role) => role.name === option.value))))
  }
  const activatePermission = (permissionId: string) => {
    if (!validatePermission(permissionId)) {
      rolePermissionsToEdit.set([...rolePermissionsToEdit.get(), permissionId])
      if (!showFooter.get() && !selectedRole.get().superAdmin) showFooter.set(true)
    }
  }
  const deactivatePermission = (permissionId: string) => {
    if (validatePermission(permissionId)) {
      rolePermissionsToEdit.set(rolePermissionsToEdit.get().filter((permission) => permission !== permissionId))
      if (!showFooter.get() && !selectedRole.get().superAdmin) showFooter.set(true)
    }
  }
  const onActiveAllModulePermissions = () => {
    if (selectedRole.get().superAdmin) return
    activatePermission(selectedModule.get().id)
    subModules.get().forEach((subModule) => {
      subModule.permissions.forEach((permission) => {
        activatePermission(permission.id)
      })
    })
  }
  const onDeactiveAllModulePermissions = () => {
    if (selectedRole.get().superAdmin) return
    deactivatePermission(selectedModule.get().id)
    subModules.get().forEach((subModule) => {
      subModule.permissions.forEach((permission) => {
        deactivatePermission(permission.id)
      })
    })
  }

  useEffect(() => {
    if (!selectedRole.get().id) {
      history.push('/user-management/roles')
      return
    }
    const options: option[] = roleList.get().map((role) => ({
      label: getRoleTranslation(role.name),
      value: role.name,
      hierarchy: role.hierarchy,
      superAdmin: role.superAdmin,
      icon: () => getRoleIcon(role.name, role.superAdmin)
    }))
    rolesOptions.set(options)
  }, [])

  useEffect(() => {
    if (modules.get().length > 0) {
      const firstModule: Module = modules.get().find((module) => module.activeForClient === true)
      const firstModuleIndex: number = modules.get().findIndex((module) => module.id === firstModule.id)
      selectedModule.set(JSON.parse(JSON.stringify(firstModule)))
      seledtedModuleIndex.set(firstModuleIndex)
    }
  }, [modules])

  useEffect(() => {
    if (selectedModule.get().id) {
      getModulePermissions(selectedModule.get().id)
    }
  }, [selectedModule])

  useEffect(() => {
    if (!selectedRole.get().superAdmin && selectedRole.get().id) {
      getRolePermissions(selectedRole.get().id)
    }
  }, [selectedRole])

  useEffect(() => {
    getModules()
  }, [])

  const _rolesOptions = [...rolesOptions.get({ noproxy: true })]

  return (
    <>
      <ConfigContainer>
        <Header>
          <HeadSection>
            <Title>
              <h1>{t('roles-permissions.config-title')}</h1>
              <p>{t('roles-permissions.config-description')}</p>
            </Title>
            <SearchableSingleSelect
              width='32.8rem'
              value={getRoleTranslation(selectedRole.get().name)}
              options={
                isRoleHierarchyActive && !currentUser.avt_role.super_admin
                  ? _rolesOptions.filter((role) => role.hierarchy > currentUser.avt_role.hierarchy)
                  : _rolesOptions
              }
              type='label'
              icon={getRoleIcon(selectedRole.get().name, selectedRole.get().superAdmin)}
              onChange={onChangeRole}
              bordered={true}
              disabled={false}
              withoutSearch={true}
              dropDownSameWidth={true}
            />
          </HeadSection>
          <DetailSection>
            <RoleDetails>
              <RoleIcon>{getRoleIcon(selectedRole.get().name, selectedRole.get().superAdmin)}</RoleIcon>
              <DetailsTitle>
                <RoleTitle>
                  <h1>{getRoleTranslation(selectedRole.get().name)}</h1>
                  {selectedRole.get().usersQuantity && (
                    <ActiveUsersTag>{`${selectedRole.get().usersQuantity} ${t(
                      'roles-permissions.users-active'
                    )}`}</ActiveUsersTag>
                  )}
                </RoleTitle>
                <p>{selectedRole.get().description}</p>
              </DetailsTitle>
            </RoleDetails>
          </DetailSection>
        </Header>
        <ConfigBody showFooter={showFooter.get()}>
          <ConfigModuleList show={showModulePermissions.get()}>
            {modules.get().map((module, index) => {
              const { icon, translation } = getIconAndTranslationByModuleId(module.id)
              return (
                <ModuleListItem
                  key={module.id}
                  selected={index === seledtedModuleIndex.get()}
                  onClick={() => onChangeModule(module, index)}
                  active={module.activeForClient}
                >
                  <div>
                    {icon} <p>{t(translation)}</p>
                  </div>
                  <span>
                    {!validatePermission(module.id) && module.activeForClient && <OffTag />}
                    <RightOutlined style={{ marginLeft: '1.6rem' }} />
                  </span>
                </ModuleListItem>
              )
            })}
          </ConfigModuleList>
          <ModulePermissions show={showModulePermissions.get()}>
            <PermissionsHeader>
              <PermissionsTitle>
                <ArrowLeftOutlined onClick={() => showModulePermissions.set(false)} />
                <div>
                  <h2>{t(getIconAndTranslationByModuleId(selectedModule.get().id).translation)}</h2>
                  <p>{selectedModule.get().description}</p>
                </div>
              </PermissionsTitle>
              <PermissionControls disabled={selectedRole.get().superAdmin}>
                <span onClick={() => onActiveAllModulePermissions()}>{t('roles-permissions.activate-all')}</span>
                <span onClick={() => onDeactiveAllModulePermissions()}>{t('roles-permissions.deactivate-all')}</span>
              </PermissionControls>
            </PermissionsHeader>
            <SubModulesList>
              <ModuleActivationCard active={validatePermission(selectedModule.get().id)}>
                <div>
                  <h3>
                    {t('roles-permissions.see-module') +
                      ' ' +
                      t(`${getIconAndTranslationByModuleId(selectedModule.get().id).translation}`)}
                  </h3>
                  <p>{t('roles-permissions.see-module-description')}</p>
                </div>
                <Switch
                  onChange={() => onChangePermission(selectedModule.get().id)}
                  checked={validatePermission(selectedModule.get().id)}
                  disabled={selectedRole.get().superAdmin}
                />
              </ModuleActivationCard>
              {validatePermission(selectedModule.get().id) &&
                subModules.get().map((subModule, index) => (
                  <CollapsibleSubModule
                    key={subModule.id}
                    title={t(`roles-permissions.sub-modules.${subModule.id}`)}
                    displayValues={displayValues}
                    collapsibleIndex={index}
                    initialState={initialState}
                  >
                    <PermissionsList>
                      {subModule.permissions.map((permission) => (
                        <PermissionItem key={permission.id}>
                          <div>
                            <p>{t(`roles-permissions.permissions.${permission.id}.name`)}</p>
                            <p>{t(`roles-permissions.permissions.${permission.id}.description`)}</p>
                          </div>
                          <Switch
                            onChange={() => onChangePermission(permission.id)}
                            checked={validatePermission(permission.id)}
                            disabled={selectedRole.get().superAdmin}
                          />
                        </PermissionItem>
                      ))}
                    </PermissionsList>
                  </CollapsibleSubModule>
                ))}
            </SubModulesList>
          </ModulePermissions>
        </ConfigBody>
        {showFooter.get() && (
          <Footer>
            <Button type='default' onClick={() => onCancelPermissionsChanged()}>
              {t('cancel')}
            </Button>
            <Button type='primary' onClick={() => onSavePermissionsChanged()} disabled={selectedRole.get().superAdmin}>
              {t('roles-permissions.save-changes')}
            </Button>
          </Footer>
        )}
      </ConfigContainer>
      {showModal.get() && (
        <UnsavedChangesModal
          showModal={showModal}
          action={() => {
            onSavePermissionsChanged()
            onChangeRole(roleOptionSelected.get())
            showModal.set(false)
          }}
        />
      )}
    </>
  )
}

export { RolesConfig }
